import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useCountUp } from 'use-count-up';
import { formatAmountWithSuffix } from '../helpers/formats';

type Props = {
    isCounting: boolean;
    className?: string;
    start?: number;
    end?: number;
    duration?: number;
    decimalPlaces?: number;
};

const StepCounter: React.FC<Props> = ({
    isCounting = true,
    className = '',
    start = 0,
    end = 0,
    duration = 2,
    decimalPlaces = 2,
}) => {
    const [current, setCurrent] = useState(['0', '00']);

    const minumumValue = useMemo(() => 100000, []);

    const getEndValue = useCallback(() => {
        if (end < minumumValue) {
            return end;
        }

        const formatted = formatAmountWithSuffix(end, undefined, minumumValue);

        return parseFloat(formatted.toString().slice(0, -1));
    }, [end, minumumValue]);

    const { value, reset } = useCountUp({
        isCounting,
        start,
        end: getEndValue(),
        duration,
        decimalPlaces,
        decimalSeparator: '.',
    });

    useEffect(() => {
        if (!value) return;

        const endLength = end > minumumValue ? 4 : 5;
        const curLength = value.toString().split('.')[0].length;

        if (curLength < endLength) {
            const missingZeros = [...new Array(endLength - curLength)]
                .map(() => '0')
                .join('');

            let suffix = '';

            if (end > minumumValue) {
                const formatted = formatAmountWithSuffix(end, undefined, minumumValue);
                suffix = formatted.toString().slice(-1);
            }

            setCurrent(`${missingZeros}${value}${suffix}`.split('.'));

            return;
        }

        setCurrent(value.toString().split('.'));
    }, [value, end, minumumValue]);

    useEffect(() => {
        if (!isCounting) {
            reset();
        }
    }, [isCounting, reset]);

    return (
        <div className={className}>
            {[...current[0]].map((value, index) => (
                <span key={index}>{value}</span>
            ))}
            <span className='dot' />
            {[...current[1]].map((decimals, index) => (
                <span key={index}>{decimals}</span>
            ))}
        </div>
    );
};

export default StepCounter;
